import React, { useCallback, useState } from 'react';
import {
  Eye, History, Edit3, Plus, Minus, Ban, MoreHorizontal, ChevronDown, BadgeCheck,
  Percent, KeyRound, Trash2, Gift, RotateCcw, ArrowDownCircle, ArrowUpCircle,
} from 'lucide-react';
import client, { createIdempotencyKey } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import ConfirmDialog from './ConfirmDialog';
import UserModal from './UserModal';
import { formatMoney } from '../lib/formatters';

/*
 * Üye işlemleri: Kullanıcılar listesindeki "İşlemler" menüsü ve arkasındaki
 * pencereler.
 *
 * Menü bir süre yalnızca listedeydi; profili açan yönetici bakiye eklemek
 * için listeye geri dönüp üyeyi yeniden aramak zorunda kalıyordu. Profil de
 * artık aynı menüyü çizer ve iki
 * ekran **aynı** kodu çalıştırır: işlem mantığı burada bir kopyadır, ayrışsa
 * profilden verilen bakiye listedekinden başka kurallarla işlenirdi.
 */

export const UserActionsMenu = ({
  user, onOpenModal, onView, onHistory, onBonus, onConfirmBan,
  onConfirmDelete, onToggleAffiliate, onClearWager, onToggleFinanceBlock,
  align = 'end',
}) => {
  const menuItemClass = 'cursor-pointer rounded-lg px-3 py-2.5 text-sm text-slate-200 focus:bg-white/[0.07] focus:text-white';
  const iconClass = 'h-4 w-4';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="group inline-flex h-9 items-center gap-2 rounded-xl border border-cyan-400/20 bg-cyan-400/[0.07] px-3 text-xs font-semibold text-cyan-300 transition-all hover:border-cyan-400/40 hover:bg-cyan-400/[0.12] hover:text-cyan-200 focus:outline-none focus:ring-2 focus:ring-cyan-400/30 data-[state=open]:border-cyan-400/50 data-[state=open]:bg-cyan-400/[0.14]"
          aria-label={`${user.username} için işlem menüsünü aç`}
        >
          <MoreHorizontal className="h-4 w-4" />
          <span>İşlemler</span>
          <ChevronDown className="h-3.5 w-3.5 text-cyan-400/70 transition-transform group-data-[state=open]:rotate-180" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={align}
        sideOffset={8}
        className="z-[100] w-56 rounded-2xl border border-white/10 bg-[#10131d]/95 p-1.5 text-slate-100 shadow-2xl shadow-black/50 backdrop-blur-xl"
      >
        <DropdownMenuLabel className="px-3 py-2">
          <span className="block max-w-[180px] truncate text-xs font-semibold text-white">{user.username}</span>
          <span className="mt-0.5 block text-[10px] font-normal uppercase tracking-wider text-slate-500">Kullanıcı işlemleri</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="mx-1 bg-white/[0.07]" />
        {/* Profilin içinde "Görüntüle" kendine döner; orada çizilmez. */}
        {onView && (
          <DropdownMenuItem className={menuItemClass} onSelect={() => onView(user)}>
            <Eye className={`${iconClass} text-cyan-400`} />
            Görüntüle
          </DropdownMenuItem>
        )}
        <DropdownMenuItem className={menuItemClass} onSelect={() => onHistory(user)}>
          <History className={`${iconClass} text-violet-400`} />
          İşlem geçmişi
        </DropdownMenuItem>
        <DropdownMenuItem className={menuItemClass} onSelect={() => onBonus(user)}>
          <Gift className={`${iconClass} text-emerald-400`} />
          Bonus kontrol
        </DropdownMenuItem>
        <DropdownMenuItem className={menuItemClass} onSelect={() => onOpenModal({ type: 'edit', user })}>
          <Edit3 className={`${iconClass} text-amber-400`} />
          Düzenle
        </DropdownMenuItem>
        <DropdownMenuItem className={menuItemClass} onSelect={() => onOpenModal({ type: 'password', user })}>
          <KeyRound className={`${iconClass} text-sky-400`} />
          Şifre sıfırla
        </DropdownMenuItem>
        <DropdownMenuSeparator className="mx-1 bg-white/[0.07]" />
        <DropdownMenuItem className={menuItemClass} onSelect={() => onOpenModal({ type: 'balance', user, sign: 1 })}>
          <Plus className={`${iconClass} text-emerald-400`} />
          Bakiye ekle
        </DropdownMenuItem>
        <DropdownMenuItem className={menuItemClass} onSelect={() => onOpenModal({ type: 'balance', user, sign: -1 })}>
          <Minus className={`${iconClass} text-orange-400`} />
          Bakiye düş
        </DropdownMenuItem>
        <DropdownMenuSeparator className="mx-1 bg-white/[0.07]" />
        {/* Kısıt hesabı kapatmaz: üye giriş yapar, sayfayı görür, yalnızca
            talep açamaz. Tümden kapatmak için "Kullanıcıyı yasakla". */}
        <DropdownMenuItem
          className={menuItemClass}
          onSelect={() => onToggleFinanceBlock(user, 'deposit_blocked')}
        >
          <ArrowDownCircle className={`${iconClass} ${user.deposit_blocked ? 'text-emerald-400' : 'text-red-400'}`} />
          {user.deposit_blocked ? 'Yatırım kısıtını kaldır' : 'Üye yatırım kısıtla'}
        </DropdownMenuItem>
        <DropdownMenuItem
          className={menuItemClass}
          onSelect={() => onToggleFinanceBlock(user, 'withdraw_blocked')}
        >
          <ArrowUpCircle className={`${iconClass} ${user.withdraw_blocked ? 'text-emerald-400' : 'text-red-400'}`} />
          {user.withdraw_blocked ? 'Çekim kısıtını kaldır' : 'Üye çekim kısıtla'}
        </DropdownMenuItem>
        {/* Bonus yasağı da hesabı kapatmaz: kampanya, kayıp bonusu,
            promosyon kodu, çark ve çapa üyeye kapanır. */}
        <DropdownMenuItem
          className={menuItemClass}
          onSelect={() => onToggleFinanceBlock(user, 'bonus_blocked')}
        >
          <Gift className={`${iconClass} ${user.bonus_blocked ? 'text-emerald-400' : 'text-red-400'}`} />
          {user.bonus_blocked ? 'Bonus yasağını kaldır' : 'Üyeye bonus yasağı koy'}
        </DropdownMenuItem>
        <DropdownMenuSeparator className="mx-1 bg-white/[0.07]" />
        <DropdownMenuItem className={menuItemClass} onSelect={() => onToggleAffiliate(user)}>
          <BadgeCheck className={`${iconClass} ${user.can_use_affiliate ? 'text-red-400' : 'text-emerald-400'}`} />
          {user.can_use_affiliate ? 'Affiliate yetkisini kaldır' : 'Affiliate yetkisi ver'}
        </DropdownMenuItem>
        <DropdownMenuItem className={menuItemClass} onSelect={() => onOpenModal({ type: 'commission', user })}>
          <Percent className={`${iconClass} text-fuchsia-400`} />
          Affiliate komisyon oranı
        </DropdownMenuItem>
        {Number(user.wager_remaining || 0) > 0 && (
          <DropdownMenuItem className={menuItemClass} onSelect={() => onClearWager(user)}>
            <RotateCcw className={`${iconClass} text-amber-400`} />
            Kalan çevrimi sıfırla
          </DropdownMenuItem>
        )}
        {(!user.is_admin || user.is_banned) && (
          <>
            <DropdownMenuSeparator className="mx-1 bg-white/[0.07]" />
            <DropdownMenuItem
              className={`${menuItemClass} ${user.is_banned ? 'text-emerald-300 focus:bg-emerald-500/10 focus:text-emerald-200' : 'text-red-300 focus:bg-red-500/10 focus:text-red-200'}`}
              onSelect={() => onConfirmBan(user)}
            >
              <Ban className={iconClass} />
              {user.is_banned ? 'Yasağı kaldır' : 'Kullanıcıyı yasakla'}
            </DropdownMenuItem>
          </>
        )}
        {!user.is_admin && (
          <DropdownMenuItem
            className={`${menuItemClass} text-red-300 focus:bg-red-500/10 focus:text-red-200`}
            onSelect={() => onConfirmDelete(user)}
          >
            <Trash2 className={iconClass} />
            Kullanıcıyı sil
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/**
 * Menünün arkasındaki durum ve pencereler.
 *
 * `reload` her başarılı işlemden sonra çağrılır (liste ya da profil yeniden
 * okunur). `onDeleted` verilirse kalıcı silmede `reload` yerine o çalışır:
 * profil silinen üyeyi yeniden okumaya kalkıp 404 ekranı çizmemelidir.
 * Arşivlenen hesap (geçmişi olan üye) silinmiş sayılmaz, yeniden okunur.
 */
export const useUserActions = ({ reload, onDeleted } = {}) => {
  // { type: 'edit'|'balance'|'create'|'password'|'commission', user }
  const [modal, setModal] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  const [deletion, setDeletion] = useState(null);
  const [wagerClear, setWagerClear] = useState(null);
  // Silme onayındaki "geçmişi de sil" seçimi.
  const [purgeHistory, setPurgeHistory] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const { toast } = useToast();

  const refresh = useCallback(async () => {
    if (reload) await reload();
  }, [reload]);

  const handleAction = async (u, action, extra = {}) => {
    if (u.is_admin && (
      action === 'delete'
      || (Object.prototype.hasOwnProperty.call(extra, 'is_banned') && extra.is_banned)
    )) {
      toast({ title: 'İşlem yapılamaz', description: 'Admin kullanıcısı yasaklanamaz veya silinemez.' });
      return false;
    }
    try {
      if (action === 'delete') {
        // Geçmişin de silinip silinmeyeceği tek onay penceresinde seçilir;
        // arka arkaya iki pencere açmak kullanıcıyı "silinmedi" sanmaya
        // itiyordu (ikinci pencere gözden kaçıyor).
        const { data } = await client.delete(`/admin/users/${u.id}`, {
          params: extra.purge ? { purge: true } : undefined,
        });
        if (data?.purged) {
          const removed = Object.entries(data.removed || {})
            .map(([label, count]) => `${count} ${label}`)
            .join(', ');
          toast({
            title: 'Kullanıcı tüm geçmişiyle silindi',
            description: removed ? `Silinen kayıtlar: ${removed}` : undefined,
          });
        } else if (data?.archived) {
          toast({
            title: 'Kullanıcı arşivlendi',
            description: `Hesapta ${data.reason || 'geçmiş kayıt'} bulunduğu için kalıcı `
              + 'silinmedi; giriş yapamayacak şekilde pasife alındı ve yasaklandı. '
              + 'Tamamen silmek için "geçmişini de sil" seçeneğini işaretleyin.',
          });
        } else toast({ title: 'Kullanıcı silindi' });
        if (onDeleted && !data?.archived) {
          setModal(null);
          onDeleted(u, data);
          return true;
        }
      } else await client.patch(
        `/admin/users/${u.id}`,
        extra,
        extra.balance_delta
          ? { headers: { 'Idempotency-Key': createIdempotencyKey() } }
          : undefined,
      );
      await refresh();
      setModal(null);
      return true;
    } catch (e) {
      toast({ title: 'Hata', description: e.response?.data?.detail || 'Bir hata oluştu' });
      return false;
    }
  };

  // Yatırım/çekim kısıtı tek tıkla açılıp kapanır: onay penceresi istemiyoruz,
  // işlem geri alınabilir ve hesabı kapatmıyor. Ne olduğu bildirimle söylenir.
  const toggleFinanceBlock = async (u, field) => {
    const next = !u[field];
    const ok = await handleAction(u, 'finance-block', { [field]: next });
    if (!ok) return;
    if (field === 'bonus_blocked') {
      toast({
        title: next ? 'Bonus yasağı kondu' : 'Bonus yasağı kaldırıldı',
        description: next
          ? `${u.username} artık hiçbir bonus, promosyon, çark ve çapa ödülü alamaz; hesabı açık kalır.`
          : `${u.username} yeniden bonus ve promosyonlardan yararlanabilir.`,
      });
      return;
    }
    const label = field === 'deposit_blocked' ? 'Yatırım' : 'Çekim';
    toast({
      title: next ? `${label} kısıtlandı` : `${label} kısıtı kaldırıldı`,
      description: next
        ? `${u.username} artık ${label.toLowerCase()} talebi oluşturamaz; hesabı açık kalır.`
        : `${u.username} yeniden ${label.toLowerCase()} talebi oluşturabilir.`,
    });
  };

  const confirmBan = async () => {
    if (!confirmation) return;
    setActionBusy(true);
    await handleAction(
      confirmation.user,
      'ban',
      { is_banned: !confirmation.user.is_banned },
    );
    setActionBusy(false);
    setConfirmation(null);
  };

  const confirmDelete = async () => {
    if (!deletion) return;
    setActionBusy(true);
    await handleAction(deletion.user, 'delete', { purge: purgeHistory });
    setActionBusy(false);
    setDeletion(null);
    setPurgeHistory(false);
  };

  // Kalan çevrim hem bonus hem kayıp bonusu kayıtlarından gelir; ikisi de
  // tek işlemde kapatılır ve kilitli tutar ana bakiyeye geçer.
  const confirmClearWager = async () => {
    if (!wagerClear) return;
    setActionBusy(true);
    try {
      const { data } = await client.post(
        `/admin/users/${wagerClear.user.id}/wager/clear`,
      );
      toast({
        title: data.cleared_count ? 'Kalan çevrim sıfırlandı' : 'Sıfırlanacak çevrim yok',
        description: data.cleared_count
          ? `${data.cleared_count} kayıt kapatıldı`
            + (data.released > 0 ? ` · ${formatMoney(data.released)} ana bakiyeye geçti.` : '.')
          : undefined,
      });
      await refresh();
    } catch (requestError) {
      toast({
        title: 'Sıfırlanamadı',
        description: requestError.response?.data?.detail || 'İşlem başarısız',
      });
    } finally {
      setActionBusy(false);
      setWagerClear(null);
    }
  };

  // `UserActionsMenu`'ye olduğu gibi yayılır; görüntüle/geçmiş/bonus
  // gezinmesi ekrana özeldir ve çağıran tarafından eklenir.
  const menuHandlers = {
    onOpenModal: setModal,
    onConfirmBan: (user) => setConfirmation({ user }),
    onConfirmDelete: (user) => setDeletion({ user }),
    onClearWager: (user) => setWagerClear({ user }),
    onToggleAffiliate: (user) => handleAction(
      user,
      'affiliate',
      { can_use_affiliate: !user.can_use_affiliate },
    ),
    onToggleFinanceBlock: toggleFinanceBlock,
  };

  const dialogs = (
    <>
      {modal && (
        <UserModal
          modal={modal}
          onClose={() => setModal(null)}
          onAction={handleAction}
          onCreated={async () => { setModal(null); await refresh(); }}
        />
      )}

      <ConfirmDialog
        open={Boolean(confirmation)}
        title={confirmation?.user.is_banned ? 'Kullanıcı engelini kaldır' : 'Kullanıcıyı yasakla'}
        description={confirmation
          ? `${confirmation.user.username} hesabı ${confirmation.user.is_banned ? 'yeniden etkinleştirilecek' : 'yasaklanacak ve oturum açması engellenecek'}.`
          : ''}
        confirmLabel={confirmation?.user.is_banned ? 'Engeli kaldır' : 'Yasakla'}
        tone={confirmation?.user.is_banned ? 'warning' : 'danger'}
        busy={actionBusy}
        onCancel={() => !actionBusy && setConfirmation(null)}
        onConfirm={confirmBan}
      />

      <ConfirmDialog
        open={Boolean(deletion)}
        title="Kullanıcıyı sil"
        description={deletion
          ? `${deletion.user.username} hesabı silinecek. Hiç hareketi yoksa kayıt `
            + 'tamamen kaldırılır. Finans, bonus veya oyun kaydı varsa önce '
            + 'pasife alınır; ardından geçmişiyle birlikte kalıcı silmek isteyip '
            + 'istemediğiniz ayrıca sorulur. Bu işlem geri alınamaz.'
          : ''}
        extra={(
          <label className="mt-1 flex cursor-pointer items-start gap-2 rounded-xl border border-red-400/25 bg-red-500/[0.07] p-3 text-left text-xs text-red-200">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={purgeHistory}
              onChange={(event) => setPurgeHistory(event.target.checked)}
              disabled={actionBusy}
            />
            <span>
              <b>Geçmişini de kalıcı olarak sil.</b> Finans hareketleri, oyun
              turları, bonuslar, kuponlar, KYC belgeleri ve ortaklık kayıtları
              da silinir; hesap arşivlenmez. Bu kayıtlar raporlardan da düşer.
            </span>
          </label>
        )}
        confirmLabel={purgeHistory ? 'Geçmişiyle birlikte sil' : 'Kullanıcıyı sil'}
        tone="danger"
        busy={actionBusy}
        onCancel={() => { if (!actionBusy) { setDeletion(null); setPurgeHistory(false); } }}
        onConfirm={confirmDelete}
      />

      <ConfirmDialog
        open={Boolean(wagerClear)}
        title="Kalan çevrimi sıfırla"
        description={wagerClear
          ? `${wagerClear.user.username} kullanıcısının kalan `
            + `${formatMoney(wagerClear.user.wager_remaining)} çevrim şartı silinecek. `
            + 'Bonus ve kayıp bonusu kayıtlarının tamamı tamamlanmış sayılır; '
            + 'kilitli bonus tutarı ana bakiyeye geçer ve çekilebilir olur. '
            + 'Bu işlem geri alınamaz.'
          : ''}
        confirmLabel="Çevrimi sıfırla"
        tone="warning"
        busy={actionBusy}
        onCancel={() => !actionBusy && setWagerClear(null)}
        onConfirm={confirmClearWager}
      />
    </>
  );

  return { openModal: setModal, menuHandlers, dialogs };
};

export default UserActionsMenu;
